import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import axios from 'axios';
import OwnerPageLayout from './layout/OwnerPageLayout';
import PendingActionsPanel from './PendingActionsPanel';
import ToastStack from './ToastStack';
import useUndoActions from '../hooks/useUndoActions';
import { downloadEventFinanceExcel } from '../utils/eventFinanceExcel';
import { getApiBaseUrl } from '../apiBase';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';

const API = getApiBaseUrl();
const DRAFT_KEY = 'event_manager_draft_v1';

export default function EventManager() {
    const { t } = useTranslation(['events', 'common', 'errors', 'messages', 'school']);
    const [school, setSchool] = useState(null);
    const [groups, setGroups] = useState([]);
    const [events, setEvents] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [summary, setSummary] = useState(null);

    const [selectedId, setSelectedId] = useState('');
    const [mainTab, setMainTab] = useState('events'); // events | finance
    const [eventTab, setEventTab] = useState('structure'); // structure | members | costs | receipts | totals
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({ q: '', city: '', groupId: '', dateFrom: '', dateTo: '' });
    const [receiptFiles, setReceiptFiles] = useState([]);

    const [structureForm, setStructureForm] = useState({
        title: '', description: '', startDate: '', endDate: '',
        city: '', location: '', roomName: '', transferDetails: '', groupIds: []
    });
    const [costsForm, setCostsForm] = useState({ ticketCost: '', transferCost: '', hotelCost: '', otherCost: '' });
    const [extraCosts, setExtraCosts] = useState([]);
    const [participantCostEdits, setParticipantCostEdits] = useState({});
    const [receiptMeta, setReceiptMeta] = useState({ receipts: [], extraCosts: [], categoryReceipts: {}, participantCategoryReceipts: {} });
    const [categoryUploadFiles, setCategoryUploadFiles] = useState({});
    const [participantUploadFiles, setParticipantUploadFiles] = useState({});
    const [participantReceiptCategory, setParticipantReceiptCategory] = useState({});
    const [bulkExtraValues, setBulkExtraValues] = useState({});
    const { pendingActions, toasts, scheduleUndoableAction, cancelPendingAction, executePendingActionNow, dismissToast } = useUndoActions(10000);
    const [pendingDraft, setPendingDraft] = useState(null);
    const [draftApplied, setDraftApplied] = useState(false);
    const [draftSavedAt, setDraftSavedAt] = useState('');
    const [participantForm, setParticipantForm] = useState({
        id: null, fullName: '', role: 'participant', groupId: '',
        tripTo: true, tripBack: true,
        ticketCost: '', transferCost: '', hotelCost: '', otherCost: '', note: ''
    });
    const [bulkForm, setBulkForm] = useState({
        role: '', groupId: '',
        ticketTo: '', ticketBack: '',
        transferMode: 'fixed', transferValue: '',
        hotelMode: 'fixed', hotelValue: '',
        otherMode: 'fixed', otherValue: ''
    });
    const [staffList, setStaffList] = useState([]);
    const [eventStaffRole, setEventStaffRole] = useState('');

    const token = localStorage.getItem('token');
    const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

    useEffect(() => {
        if (!eventStaffRole) setEventStaffRole(t('events:staffRoleOrganizer'));
    }, [t, eventStaffRole]);

    useEffect(() => {
        let cancelled = false;
        loadData(() => cancelled);
        return () => { cancelled = true; };
    }, []);
    useEffect(() => { if (selectedId) loadParticipants(selectedId); else setParticipants([]); }, [selectedId]);
    useEffect(() => { if (school?.id) loadSummary(); }, [school, filters.dateFrom, filters.dateTo]);
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            setPendingDraft(parsed);
        } catch { }
    }, []);


    async function loadData(isCancelled = () => false) {
        setLoading(true);
        try {
            const schoolRes = await getSchoolForSession(api);
            const schoolId = getSchoolIdFromResponse(schoolRes);
            setSchool(schoolRes.data);
            const [groupsRes, eventsRes, staffRes] = await Promise.all([
                api.get(`/school/${schoolId}/groups`),
                api.get('/event', {
                    params: {
                        schoolId,
                        dateFrom: filters.dateFrom || undefined,
                        dateTo: filters.dateTo || undefined,
                        city: filters.city || undefined,
                        groupId: filters.groupId || undefined
                    }
                }),
                api.get('/staff', { params: { schoolId } })
            ]);
            setGroups(groupsRes.data || []);
            setEvents(eventsRes.data || []);
            setStaffList((staffRes.data || []).filter(s => s.isActive !== false));
        } catch (err) {
            if (!isCancelled()) {
                alert(err.response?.status === 403
                    ? t('events:noAccess')
                    : t('events:loadError'));
            }
        }
        if (!isCancelled()) setLoading(false);
    }

    async function loadSummary() {
        if (!school?.id) return;
        try {
            const res = await api.get('/event/finance/summary', {
                params: { schoolId: school.id, dateFrom: filters.dateFrom || undefined, dateTo: filters.dateTo || undefined }
            });
            setSummary(res.data);
        } catch { }
    }

    async function loadParticipants(eventId) {
        try {
            const res = await api.get(`/event/${eventId}/participants`);
            setParticipants(res.data || []);
        } catch {
            setParticipants([]);
        }
    }

    async function assignStaffToSelectedEvent(staffId) {
        if (!selectedEvent || !staffId) return;
        try {
            await api.post('/staff/assign/event', {
                staffId: parseInt(staffId, 10),
                eventId: selectedEvent.id,
                roleInEvent: eventStaffRole.trim() || null
            });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:staffAssignError'));
        }
    }

    async function unassignStaffFromSelectedEvent(staffId) {
        if (!selectedEvent) return;
        try {
            await api.delete('/staff/assign/event', { params: { staffId, eventId: selectedEvent.id } });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:staffUnassignError'));
        }
    }

    const selectedEvent = useMemo(() => {
        if (!selectedId) return null;
        return events.find(e => e.id === parseInt(selectedId, 10)) || null;
    }, [selectedId, events]);

    const filteredEvents = useMemo(() => {
        return events.filter(ev => {
            const text = `${ev.title || ''} ${ev.city || ''} ${ev.location || ''}`.toLowerCase();
            return !filters.q || text.includes(filters.q.toLowerCase());
        });
    }, [events, filters.q]);

    useEffect(() => {
        if (!selectedEvent) {
            setStructureForm({
                title: '', description: '', startDate: '', endDate: '',
                city: '', location: '', roomName: '', transferDetails: '', groupIds: []
            });
            setCostsForm({ ticketCost: '', transferCost: '', hotelCost: '', otherCost: '' });
            setExtraCosts([]);
            setReceiptMeta({ receipts: [], extraCosts: [], categoryReceipts: {}, participantCategoryReceipts: {} });
            return;
        }
        setStructureForm({
            title: selectedEvent.title || '',
            description: selectedEvent.description || '',
            startDate: selectedEvent.startDate ? String(selectedEvent.startDate).slice(0, 10) : '',
            endDate: selectedEvent.endDate ? String(selectedEvent.endDate).slice(0, 10) : '',
            city: selectedEvent.city || '',
            location: selectedEvent.location || '',
            roomName: selectedEvent.roomName || '',
            transferDetails: selectedEvent.transferDetails || '',
            groupIds: (selectedEvent.groupIds || []).map(x => String(x))
        });
        setCostsForm({
            ticketCost: selectedEvent.ticketCost ?? '',
            transferCost: selectedEvent.transferCost ?? '',
            hotelCost: selectedEvent.hotelCost ?? '',
            otherCost: selectedEvent.otherCost ?? ''
        });
        const meta = normalizeReceiptMeta(selectedEvent.receiptMeta || { receipts: selectedEvent.receipts || [], extraCosts: [], categoryReceipts: {}, participantCategoryReceipts: {} });
        setReceiptMeta(meta);
        setExtraCosts(meta.extraCosts || []);
        const nextBulkExtra = {};
        (meta.extraCosts || []).forEach((x) => {
            nextBulkExtra[x.id] = { mode: 'fixed', value: '' };
        });
        setBulkExtraValues(nextBulkExtra);
    }, [selectedEvent]);

    useEffect(() => {
        const next = {};
        participants.forEach((p) => {
            next[p.id] = {
                ticketCost: p.ticketCost ?? '',
                transferCost: p.transferCost ?? '',
                hotelCost: p.hotelCost ?? '',
                otherCost: p.otherCost ?? '',
                extra: {}
            };
            (extraCosts || []).forEach((x) => { next[p.id].extra[x.id] = ''; });
        });
        setParticipantCostEdits(next);
    }, [participants, extraCosts]);

    useEffect(() => {
        if (loading || !pendingDraft || draftApplied) return;
        if (pendingDraft.filters) setFilters(pendingDraft.filters);
        if (pendingDraft.mainTab) setMainTab(pendingDraft.mainTab);
        if (pendingDraft.eventTab) setEventTab(pendingDraft.eventTab);
        if (pendingDraft.selectedId) {
            const exists = events.some(e => String(e.id) === String(pendingDraft.selectedId));
            if (exists) setSelectedId(String(pendingDraft.selectedId));
        } else {
            if (pendingDraft.structureForm) setStructureForm(pendingDraft.structureForm);
            if (pendingDraft.costsForm) setCostsForm(pendingDraft.costsForm);
            if (pendingDraft.extraCosts) setExtraCosts(pendingDraft.extraCosts);
            if (pendingDraft.receiptMeta) setReceiptMeta(pendingDraft.receiptMeta);
            if (pendingDraft.bulkForm) setBulkForm(pendingDraft.bulkForm);
            if (pendingDraft.participantCostEdits) setParticipantCostEdits(pendingDraft.participantCostEdits);
            setDraftApplied(true);
        }
    }, [loading, pendingDraft, draftApplied, events]);

    useEffect(() => {
        if (!pendingDraft || draftApplied === true || !selectedEvent) return;
        if (String(pendingDraft.selectedId || '') !== String(selectedEvent.id)) return;
        if (pendingDraft.structureForm) setStructureForm(pendingDraft.structureForm);
        if (pendingDraft.costsForm) setCostsForm(pendingDraft.costsForm);
        if (pendingDraft.extraCosts) setExtraCosts(pendingDraft.extraCosts);
        if (pendingDraft.receiptMeta) setReceiptMeta(pendingDraft.receiptMeta);
        if (pendingDraft.bulkForm) setBulkForm(pendingDraft.bulkForm);
        if (pendingDraft.participantCostEdits) setParticipantCostEdits(pendingDraft.participantCostEdits);
        setDraftApplied(true);
    }, [selectedEvent, pendingDraft, draftApplied]);

    useEffect(() => {
        const payload = {
            selectedId,
            mainTab,
            eventTab,
            filters,
            structureForm,
            costsForm,
            extraCosts,
            receiptMeta,
            bulkForm,
            participantCostEdits,
            savedAt: new Date().toISOString()
        };
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
            setDraftSavedAt(payload.savedAt);
        } catch { }
    }, [selectedId, mainTab, eventTab, filters, structureForm, costsForm, extraCosts, receiptMeta, bulkForm, participantCostEdits]);

    function clearDraft() {
        try {
            localStorage.removeItem(DRAFT_KEY);
        } catch { }
        setPendingDraft(null);
        setDraftSavedAt('');
        setDraftApplied(false);
    }


    async function handleCreateEvent(e) {
        e.preventDefault();
        try {
            const payload = {
                title: structureForm.title,
                description: structureForm.description || null,
                startDate: structureForm.startDate || new Date().toISOString(),
                endDate: structureForm.endDate || null,
                city: structureForm.city || '',
                location: structureForm.location || '',
                roomName: structureForm.roomName || null,
                transferDetails: structureForm.transferDetails || null,
                ticketCost: 0, transferCost: 0, hotelCost: 0, otherCost: 0,
                receiptsJson: JSON.stringify(receiptMeta), groupId: null,
                groupIds: structureForm.groupIds.map(x => parseInt(x, 10)).filter(x => !Number.isNaN(x))
            };
            const res = await api.post('/event', payload, { params: { schoolId: school?.id } });
            await loadData();
            if (res?.data?.id) setSelectedId(String(res.data.id));
            setEventTab('members');
        } catch (err) {
            alert(err.response?.data?.message || t('events:createError'));
        }
    }

    async function handleUpdateStructure(e) {
        e.preventDefault();
        if (!selectedEvent) return;
        try {
            await api.put(`/event/${selectedEvent.id}`, {
                title: structureForm.title,
                description: structureForm.description || null,
                startDate: structureForm.startDate || new Date().toISOString(),
                endDate: structureForm.endDate || null,
                city: structureForm.city || '',
                location: structureForm.location || '',
                roomName: structureForm.roomName || null,
                transferDetails: structureForm.transferDetails || null,
                ticketCost: selectedEvent.ticketCost || 0,
                transferCost: selectedEvent.transferCost || 0,
                hotelCost: selectedEvent.hotelCost || 0,
                otherCost: selectedEvent.otherCost || 0,
                receiptsJson: JSON.stringify({ ...receiptMeta, extraCosts }), groupId: null,
                groupIds: structureForm.groupIds.map(x => parseInt(x, 10)).filter(x => !Number.isNaN(x))
            });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:structureSaveError'));
        }
    }

    async function handleUpdateCommonCosts() {
        if (!selectedEvent) return;
        try {
            const normalizedMeta = normalizeReceiptMeta(receiptMeta);
            const normalizedExtra = (extraCosts || [])
                .filter(x => String(x.name || '').trim() !== '' || String(x.amount || '').trim() !== '')
                .map(x => ({ id: String(x.id), name: String(x.name || '').trim(), amount: x.amount === '' ? 0 : parseFloat(x.amount || 0) }));
            const extraSum = extraCosts.reduce((sum, item) => {
                const v = parseFloat(item.amount);
                return sum + (Number.isNaN(v) ? 0 : v);
            }, 0);
            await api.put(`/event/${selectedEvent.id}`, {
                title: selectedEvent.title,
                description: selectedEvent.description || null,
                startDate: selectedEvent.startDate,
                endDate: selectedEvent.endDate || null,
                city: selectedEvent.city || '',
                location: selectedEvent.location || '',
                roomName: selectedEvent.roomName || null,
                transferDetails: selectedEvent.transferDetails || null,
                ticketCost: costsForm.ticketCost ? parseFloat(costsForm.ticketCost) : 0,
                transferCost: costsForm.transferCost ? parseFloat(costsForm.transferCost) : 0,
                hotelCost: costsForm.hotelCost ? parseFloat(costsForm.hotelCost) : 0,
                otherCost: (costsForm.otherCost ? parseFloat(costsForm.otherCost) : 0) + extraSum,
                receiptsJson: JSON.stringify({ ...normalizedMeta, extraCosts: normalizedExtra }), groupId: null,
                groupIds: (selectedEvent.groupIds || [])
            });
            setReceiptMeta({ ...normalizedMeta, extraCosts: normalizedExtra });
            setExtraCosts(normalizedExtra);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:costsSaveError'));
        }
    }

    async function handleSaveParticipantCosts(participantId) {
        if (!selectedEvent || !participantId) return;
        const p = participants.find(x => x.id === participantId);
        const edit = participantCostEdits[participantId];
        if (!p || !edit) return;
        try {
            await api.put(`/event/participants/${participantId}`, {
                fullName: p.fullName,
                role: p.role,
                groupId: p.groupId ?? null,
                tripTo: !!p.tripTo,
                tripBack: !!p.tripBack,
                ticketCost: edit.ticketCost ? parseFloat(edit.ticketCost) : 0,
                transferCost: edit.transferCost ? parseFloat(edit.transferCost) : 0,
                hotelCost: edit.hotelCost ? parseFloat(edit.hotelCost) : 0,
                otherCost: extraIdList.reduce((sum, id) => sum + (parseFloat(edit?.extra?.[id]) || 0), 0),
                note: p.note || null
            });
            await loadParticipants(selectedEvent.id);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:participantSaveError'));
        }
    }

    async function handleSaveAllParticipantCosts() {
        if (!selectedEvent || participants.length === 0) return;
        try {
            const requests = participants.map((p) => {
                const edit = participantCostEdits[p.id] || {};
                return api.put(`/event/participants/${p.id}`, {
                    fullName: p.fullName,
                    role: p.role,
                    groupId: p.groupId ?? null,
                    tripTo: !!p.tripTo,
                    tripBack: !!p.tripBack,
                    ticketCost: edit.ticketCost ? parseFloat(edit.ticketCost) : 0,
                    transferCost: edit.transferCost ? parseFloat(edit.transferCost) : 0,
                    hotelCost: edit.hotelCost ? parseFloat(edit.hotelCost) : 0,
                    otherCost: extraIdList.reduce((sum, id) => sum + (parseFloat(edit?.extra?.[id]) || 0), 0),
                    note: p.note || null
                });
            });
            await Promise.all(requests);
            await loadParticipants(selectedEvent.id);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:bulkSaveError'));
        }
    }

    async function handleDeactivate() {
        if (!selectedEvent) return;
        scheduleUndoableAction(t('events:deactivateUndo', { title: selectedEvent.title }), async () => {
            try {
                await api.post(`/event/${selectedEvent.id}/deactivate`);
                setSelectedId('');
                setEventTab('structure');
                await loadData();
            } catch (err) {
                alert(err.response?.data?.message || t('events:deactivateError'));
            }
        });
    }

    async function handleSaveParticipant(e) {
        e.preventDefault();
        if (!selectedEvent) return;
        try {
            const payload = {
                fullName: participantForm.fullName,
                role: participantForm.role,
                groupId: participantForm.groupId ? parseInt(participantForm.groupId, 10) : null,
                tripTo: !!participantForm.tripTo,
                tripBack: !!participantForm.tripBack,
                ticketCost: participantForm.ticketCost ? parseFloat(participantForm.ticketCost) : 0,
                transferCost: participantForm.transferCost ? parseFloat(participantForm.transferCost) : 0,
                hotelCost: participantForm.hotelCost ? parseFloat(participantForm.hotelCost) : 0,
                otherCost: participantForm.otherCost ? parseFloat(participantForm.otherCost) : 0,
                note: participantForm.note || null
            };
            if (participantForm.id) await api.put(`/event/participants/${participantForm.id}`, payload);
            else await api.post(`/event/${selectedEvent.id}/participants`, payload);

            setParticipantForm({
                id: null, fullName: '', role: 'participant', groupId: '',
                tripTo: true, tripBack: true, ticketCost: '', transferCost: '', hotelCost: '', otherCost: '', note: ''
            });
            await loadParticipants(selectedEvent.id);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:participantSaveError2'));
        }
    }

    async function handleDeleteParticipant(id) {
        const p = participants.find(x => x.id === id);
        scheduleUndoableAction(t('events:participantDeleteUndo', { name: p?.fullName || id }), async () => {
            try {
                await api.delete(`/event/participants/${id}`);
                if (selectedEvent) {
                    await loadParticipants(selectedEvent.id);
                    await loadData();
                }
            } catch (err) {
                alert(err.response?.data?.message || t('events:participantDeleteError'));
            }
        });
    }

    async function handleImportFromGroups() {
        if (!selectedEvent) return;
        const groupIds = structureForm.groupIds.map(x => parseInt(x, 10)).filter(x => !Number.isNaN(x));
        if (groupIds.length === 0) return alert(t('events:pickGroups'));
        try {
            await api.post(`/event/${selectedEvent.id}/participants/import-groups`, { groupIds });
            await loadParticipants(selectedEvent.id);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:importError'));
        }
    }

    async function handleApplyBulk() {
        if (!selectedEvent) return;
        try {
            await api.post(`/event/${selectedEvent.id}/participants/bulk-costs`, {
                role: bulkForm.role || null,
                groupId: bulkForm.groupId ? parseInt(bulkForm.groupId, 10) : null,
                participantIds: null,
                ticketTo: bulkForm.ticketTo ? parseFloat(bulkForm.ticketTo) : null,
                ticketBack: bulkForm.ticketBack ? parseFloat(bulkForm.ticketBack) : null,
                transferMode: bulkForm.transferValue ? bulkForm.transferMode : null,
                transferValue: bulkForm.transferValue ? parseFloat(bulkForm.transferValue) : null,
                hotelMode: bulkForm.hotelValue ? bulkForm.hotelMode : null,
                hotelValue: bulkForm.hotelValue ? parseFloat(bulkForm.hotelValue) : null,
                otherMode: null,
                otherValue: null
            });
            const targetIds = participants
                .filter(p => (!bulkForm.role || p.role === bulkForm.role) && (!bulkForm.groupId || String(p.groupId || '') === String(bulkForm.groupId)))
                .map(p => p.id);
            if (targetIds.length > 0) {
                const updated = { ...participantCostEdits };
                const targetCount = targetIds.length;
                (extraCosts || []).forEach((x) => {
                    const cfg = bulkExtraValues[x.id];
                    if (!cfg || cfg.value === '' || cfg.value === null || cfg.value === undefined) return;
                    const raw = parseFloat(cfg.value);
                    if (Number.isNaN(raw)) return;
                    const per = cfg.mode === 'split' ? (targetCount === 0 ? 0 : raw / targetCount) : raw;
                    targetIds.forEach(pid => {
                        if (!updated[pid]) updated[pid] = { ticketCost: '', transferCost: '', hotelCost: '', otherCost: '', extra: {} };
                        if (!updated[pid].extra) updated[pid].extra = {};
                        updated[pid].extra[x.id] = String(per);
                    });
                });
                setParticipantCostEdits(updated);
            }
            await loadParticipants(selectedEvent.id);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:bulkFillError'));
        }
    }

    async function handleUploadReceipts() {
        if (!selectedEvent) return alert(t('events:pickEventFirst'));
        if (!receiptFiles || receiptFiles.length === 0) return alert(t('events:pickReceiptFiles'));
        const fd = new FormData();
        Array.from(receiptFiles).forEach(f => fd.append('files', f));
        try {
            await api.post(`/event/${selectedEvent.id}/receipts`, fd, { headers: { Authorization: `Bearer ${token}` } });
            setReceiptFiles([]);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:receiptUploadError'));
        }
    }

    async function handleDeleteReceipt(path) {
        if (!selectedEvent) return;
        scheduleUndoableAction(t('events:receiptDeleteUndo', { name: path.split('/').pop() || t('events:fileFallback') }), async () => {
            try {
                await api.delete(`/event/${selectedEvent.id}/receipts`, { data: { path } });
                await loadData();
            } catch (err) {
                alert(err.response?.data?.message || t('events:receiptDeleteError'));
            }
        });
    }

    async function handleUploadCategoryReceipts(expenseKey) {
        if (!selectedEvent) return;
        const files = categoryUploadFiles[expenseKey];
        if (!files || files.length === 0) return alert(t('events:pickReceiptFiles'));
        const fd = new FormData();
        Array.from(files).forEach(f => fd.append('files', f));
        fd.append('expenseKey', expenseKey);
        try {
            await api.post(`/event/${selectedEvent.id}/receipts`, fd, { headers: { Authorization: `Bearer ${token}` } });
            setCategoryUploadFiles({ ...categoryUploadFiles, [expenseKey]: null });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:receiptUploadSingleError'));
        }
    }

    async function handleUploadParticipantReceipts(participantId) {
        if (!selectedEvent) return;
        const files = participantUploadFiles[participantId];
        if (!files || files.length === 0) return alert(t('events:pickReceiptFiles'));
        const expenseKey = participantReceiptCategory[participantId] || 'ticket';
        const fd = new FormData();
        Array.from(files).forEach(f => fd.append('files', f));
        fd.append('expenseKey', expenseKey);
        fd.append('participantId', String(participantId));
        try {
            await api.post(`/event/${selectedEvent.id}/receipts`, fd, { headers: { Authorization: `Bearer ${token}` } });
            setParticipantUploadFiles({ ...participantUploadFiles, [participantId]: null });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('events:participantReceiptError'));
        }
    }

    async function handleRemoveExtraCostWithRecalc(extraId) {
        if (!selectedEvent) return;
        const removed = (extraCosts || []).find(x => x.id === extraId);
        scheduleUndoableAction(t('events:extraCostDeleteUndo', { name: removed?.name || t('events:unnamed') }), async () => {
            try {
                const nextExtraCosts = (extraCosts || []).filter(x => x.id !== extraId);
                const nextBulkExtra = { ...bulkExtraValues };
                delete nextBulkExtra[extraId];

                const nextEdits = { ...participantCostEdits };
                participants.forEach((p) => {
                    const current = nextEdits[p.id] || { ticketCost: p.ticketCost ?? '', transferCost: p.transferCost ?? '', hotelCost: p.hotelCost ?? '', otherCost: p.otherCost ?? '', extra: {} };
                    const extraMap = { ...(current.extra || {}) };
                    delete extraMap[extraId];
                    nextEdits[p.id] = { ...current, extra: extraMap };
                });

                const normalizedMeta = normalizeReceiptMeta(receiptMeta);
                const nextCategoryReceipts = { ...(normalizedMeta.categoryReceipts || {}) };
                delete nextCategoryReceipts[`extra:${extraId}`];
                const nextReceiptMeta = { ...normalizedMeta, extraCosts: nextExtraCosts, categoryReceipts: nextCategoryReceipts };

                const saveParticipants = participants.map((p) => {
                    const edit = nextEdits[p.id] || {};
                    const newOther = nextExtraCosts.reduce((sum, item) => sum + (parseFloat(edit?.extra?.[item.id]) || 0), 0);
                    return api.put(`/event/participants/${p.id}`, {
                        fullName: p.fullName,
                        role: p.role,
                        groupId: p.groupId ?? null,
                        tripTo: !!p.tripTo,
                        tripBack: !!p.tripBack,
                        ticketCost: edit.ticketCost ? parseFloat(edit.ticketCost) : 0,
                        transferCost: edit.transferCost ? parseFloat(edit.transferCost) : 0,
                        hotelCost: edit.hotelCost ? parseFloat(edit.hotelCost) : 0,
                        otherCost: newOther,
                        note: p.note || null
                    });
                });
                await Promise.all(saveParticipants);

                const extraSum = nextExtraCosts.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                await api.put(`/event/${selectedEvent.id}`, {
                    title: selectedEvent.title,
                    description: selectedEvent.description || null,
                    startDate: selectedEvent.startDate,
                    endDate: selectedEvent.endDate || null,
                    city: selectedEvent.city || '',
                    location: selectedEvent.location || '',
                    roomName: selectedEvent.roomName || null,
                    transferDetails: selectedEvent.transferDetails || null,
                    ticketCost: costsForm.ticketCost ? parseFloat(costsForm.ticketCost) : 0,
                    transferCost: costsForm.transferCost ? parseFloat(costsForm.transferCost) : 0,
                    hotelCost: costsForm.hotelCost ? parseFloat(costsForm.hotelCost) : 0,
                    otherCost: (costsForm.otherCost ? parseFloat(costsForm.otherCost) : 0) + extraSum,
                    receiptsJson: JSON.stringify(nextReceiptMeta),
                    groupId: null,
                    groupIds: (selectedEvent.groupIds || [])
                });

                setBulkExtraValues(nextBulkExtra);
                setParticipantCostEdits(nextEdits);
                setExtraCosts(nextExtraCosts);
                setReceiptMeta(nextReceiptMeta);
                await loadParticipants(selectedEvent.id);
                await loadData();
            } catch (err) {
                alert(err.response?.data?.message || t('events:extraCostDeleteError'));
            }
        });
    }

    async function handleExportExcel() {
        if (!school?.id) return;
        try {
            const res = await api.get('/event/finance/export-details', {
                params: { schoolId: school.id, dateFrom: filters.dateFrom || undefined, dateTo: filters.dateTo || undefined }
            });
            downloadEventFinanceExcel(res.data);
        } catch (err) {
            alert(err.response?.data?.message || t('events:exportError'));
        }
    }

    const participantsTotal = participants.reduce((sum, p) => sum + (p.totalCost || 0), 0);
    const commonTotal = (selectedEvent?.ticketCost || 0) + (selectedEvent?.transferCost || 0) + (selectedEvent?.hotelCost || 0) + (selectedEvent?.otherCost || 0);
    const eventExpenseKeys = [
        { key: 'ticket', label: t('events:categories.ticket') },
        { key: 'transfer', label: t('events:categories.transfer') },
        { key: 'hotel', label: t('events:categories.hotel') },
        { key: 'other_base', label: t('events:categories.otherBase') },
        ...(extraCosts || []).map(x => ({ key: `extra:${x.id}`, label: t('events:categories.extraCost', { name: x.name || t('events:unnamed') }) }))
    ];
    const extraIdList = (extraCosts || []).map(x => x.id);

    if (loading) {
        return (
            <>
                <ToastStack toasts={toasts} onDismiss={dismissToast} />
                <OwnerPageLayout title={t('events:title')} maxWidth={1180}>
                    <div className="sp-loading-page">{t('events:loading')}</div>
                </OwnerPageLayout>
            </>
        );
    }

    const draftNote = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ color: 'var(--sp-gray-600)', fontSize: 12 }}>
                {draftSavedAt
                    ? t('events:draftSaved', { date: formatDateTime(draftSavedAt) })
                    : t('events:draftNotSaved')}
            </div>
            <button type="button" className="sp-btn sp-btn--ghost" onClick={clearDraft}>{t('events:clearDraft')}</button>
        </div>
    );

    return (
        <>
            <ToastStack toasts={toasts} onDismiss={dismissToast} />
            <OwnerPageLayout
                title={t('events:pageTitle')}
                subtitle={t('events:pageSubtitle', { school: school?.name || '' })}
                maxWidth={1180}
            >
                {draftNote}
                <PendingActionsPanel
                    pendingActions={pendingActions}
                    onCancel={cancelPendingAction}
                    onExecuteNow={executePendingActionNow}
                    title={t('events:pendingDeletes')}
                    containerStyle={{ ...card, border: '1px solid #fde68a', background: '#fffbeb', marginBottom: '10px', padding: '10px' }}
                    rowButtonGhostStyle={ghostBtn}
                    rowButtonDangerStyle={dangerBtn}
                />

                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button onClick={() => setMainTab('events')} style={tabBtn(mainTab === 'events')}>{t('events:tabEventsPanel')}</button>
                    <button onClick={() => setMainTab('finance')} style={tabBtn(mainTab === 'finance')}>{t('events:tabFinance')}</button>
                </div>

                {mainTab === 'finance' ? (
                    <div style={{ ...card, marginTop: '14px' }}>
                        <h3 style={{ marginTop: 0 }}>{t('events:analyticsTitle')}</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '10px' }}>
                            <input type="date" value={filters.dateFrom} onChange={e => setFilters({ ...filters, dateFrom: e.target.value })} style={input} />
                            <input type="date" value={filters.dateTo} onChange={e => setFilters({ ...filters, dateTo: e.target.value })} style={input} />
                            <button onClick={handleExportExcel} style={primaryBtn}>{t('events:exportExcel')}</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                            <Kpi label={t('events:kpiEvents')} value={summary?.eventsCount ?? 0} />
                            <Kpi label={t('events:kpiParticipants')} value={summary?.participantsCount ?? 0} />
                            <Kpi label={t('events:kpiCommonCosts')} value={`${(summary?.commonCosts ?? 0).toLocaleString('ru-RU')} ₽`} />
                            <Kpi label={t('events:kpiPersonalCosts')} value={`${(summary?.personCosts ?? 0).toLocaleString('ru-RU')} ₽`} />
                            <Kpi label={t('events:kpiTotal')} value={`${(summary?.totalCosts ?? 0).toLocaleString('ru-RU')} ₽`} />
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ ...card, marginTop: '14px' }}>
                            <h3 style={{ marginTop: 0 }}>{t('events:searchTitle')}</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '8px', marginBottom: '8px' }}>
                                <input placeholder={t('events:searchPlaceholder')} value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} style={input} />
                                <input placeholder={t('events:city')} value={filters.city} onChange={e => setFilters({ ...filters, city: e.target.value })} style={input} />
                                <select value={filters.groupId} onChange={e => setFilters({ ...filters, groupId: e.target.value })} style={input}>
                                    <option value="">{t('events:allGroups')}</option>
                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </select>
                                <button onClick={loadData} style={primaryBtn}>{t('events:apply')}</button>
                            </div>
                            <select
                                value={selectedId}
                                onChange={e => {
                                    setSelectedId(e.target.value);
                                    setEventTab('structure');
                                }}
                                style={input}
                            >
                                <option value="">{t('events:newEvent')}</option>
                                {filteredEvents.map(ev => (
                                    <option key={ev.id} value={ev.id}>
                                        {ev.title} · {formatDate(ev.startDate)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ ...card, marginTop: '14px' }}>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                                <button onClick={() => setEventTab('structure')} style={tabBtn(eventTab === 'structure')}>{t('events:tabStructure')}</button>
                                <button onClick={() => setEventTab('members')} style={tabBtn(eventTab === 'members')} disabled={!selectedEvent}>{t('events:tabMembers')}</button>
                                <button onClick={() => setEventTab('costs')} style={tabBtn(eventTab === 'costs')} disabled={!selectedEvent}>{t('events:tabCosts')}</button>
                                <button onClick={() => setEventTab('receipts')} style={tabBtn(eventTab === 'receipts')} disabled={!selectedEvent}>{t('events:tabReceipts')}</button>
                                <button onClick={() => setEventTab('totals')} style={tabBtn(eventTab === 'totals')} disabled={!selectedEvent}>{t('events:tabTotals')}</button>
                            </div>

                            {eventTab === 'structure' && (
                                <>
                                    <form onSubmit={selectedEvent ? handleUpdateStructure : handleCreateEvent} style={{ display: 'grid', gap: '8px' }}>
                                        <h3 style={{ margin: 0 }}>{selectedEvent ? t('events:structureTitle') : t('events:createNoCosts')}</h3>
                                        <input required placeholder={t('events:namePlaceholder')} value={structureForm.title} onChange={e => setStructureForm({ ...structureForm, title: e.target.value })} style={input} />
                                        <input placeholder={t('events:description')} value={structureForm.description} onChange={e => setStructureForm({ ...structureForm, description: e.target.value })} style={input} />
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                            <input type="date" value={structureForm.startDate} onChange={e => setStructureForm({ ...structureForm, startDate: e.target.value })} style={input} />
                                            <input type="date" value={structureForm.endDate} onChange={e => setStructureForm({ ...structureForm, endDate: e.target.value })} style={input} />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                            <input placeholder={t('events:city')} value={structureForm.city} onChange={e => setStructureForm({ ...structureForm, city: e.target.value })} style={input} />
                                            <input placeholder={t('events:location')} value={structureForm.location} onChange={e => setStructureForm({ ...structureForm, location: e.target.value })} style={input} />
                                        </div>
                                        <input placeholder={t('events:room')} value={structureForm.roomName} onChange={e => setStructureForm({ ...structureForm, roomName: e.target.value })} style={input} />
                                        <input placeholder={t('events:transferDetails')} value={structureForm.transferDetails} onChange={e => setStructureForm({ ...structureForm, transferDetails: e.target.value })} style={input} />
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{t('events:eventGroupsLabel')}</div>
                                            <select
                                                multiple
                                                value={structureForm.groupIds}
                                                onChange={e => {
                                                    const values = Array.from(e.target.selectedOptions).map(o => o.value);
                                                    setStructureForm({ ...structureForm, groupIds: values });
                                                }}
                                                style={{ ...input, minHeight: '90px' }}
                                            >
                                                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button type="submit" style={primaryBtn}>{selectedEvent ? t('events:saveStructure') : t('events:createAndGoMembers')}</button>
                                            {selectedEvent && <button type="button" onClick={handleDeactivate} style={dangerBtn}>{t('events:deactivate')}</button>}
                                        </div>
                                    </form>

                                    {selectedEvent && (
                                        <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid var(--sp-gray-100)' }}>
                                            <h4 style={{ margin: '0 0 6px', fontSize: '15px' }}>{t('events:staffTeamTitle')}</h4>
                                            <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#64748b', lineHeight: 1.4 }}>
                                                {t('events:staffTeamHint')}
                                            </p>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                                                <input
                                                    placeholder={t('events:roleInEvent')}
                                                    value={eventStaffRole}
                                                    onChange={e => setEventStaffRole(e.target.value)}
                                                    style={{ ...input, maxWidth: '200px' }}
                                                />
                                                <select
                                                    defaultValue=""
                                                    onChange={e => {
                                                        const v = e.target.value;
                                                        e.target.value = '';
                                                        if (v) assignStaffToSelectedEvent(v);
                                                    }}
                                                    style={{ ...input, flex: '1 1 200px', minWidth: '160px' }}
                                                >
                                                    <option value="">{t('events:addStaffOption')}</option>
                                                    {staffList
                                                        .filter(s => !(selectedEvent.staff || []).some(x => x.staffMemberId === s.id))
                                                        .map(s => (
                                                            <option key={s.id} value={s.id}>{s.fullName}{s.role ? ` (${s.role})` : ''}</option>
                                                        ))}
                                                </select>
                                            </div>
                                            {(selectedEvent.staff || []).length === 0 ? (
                                                <div style={{ fontSize: '13px', color: '#94a3b8' }}>{t('events:noStaffAssigned')}</div>
                                            ) : (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                    {(selectedEvent.staff || []).map(st => (
                                                        <span
                                                            key={st.staffMemberId}
                                                            style={{
                                                                fontSize: '13px',
                                                                padding: '6px 10px',
                                                                borderRadius: '999px',
                                                                background: '#eef2ff',
                                                                color: '#312e81',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '8px'
                                                            }}
                                                        >
                                                            <span>{st.fullName}{st.roleInEvent ? ` — ${st.roleInEvent}` : ''}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => unassignStaffFromSelectedEvent(st.staffMemberId)}
                                                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: '#94a3b8', fontSize: '16px', lineHeight: 1 }}
                                                                title={t('events:unassign')}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {eventTab === 'members' && selectedEvent && (
                                <>
                                    <h3 style={{ marginTop: 0 }}>{t('events:membersTitle')}</h3>
                                    <div style={{ marginBottom: '8px' }}>
                                        <button onClick={handleImportFromGroups} style={ghostBtn}>{t('events:importFromGroups')}</button>
                                    </div>
                                    <form onSubmit={handleSaveParticipant} style={{ display: 'grid', gap: '8px', marginBottom: '10px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                                            <input required placeholder={t('events:fullName')} value={participantForm.fullName} onChange={e => setParticipantForm({ ...participantForm, fullName: e.target.value })} style={input} />
                                            <select value={participantForm.role} onChange={e => setParticipantForm({ ...participantForm, role: e.target.value })} style={input}>
                                                <option value="participant">{t('events:roleParticipant')}</option>
                                                <option value="staff">{t('events:roleStaff')}</option>
                                                <option value="companion">{t('events:roleCompanion')}</option>
                                            </select>
                                            <select value={participantForm.groupId} onChange={e => setParticipantForm({ ...participantForm, groupId: e.target.value })} style={input}>
                                                <option value="">{t('events:noGroupFree')}</option>
                                                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <label style={{ fontSize: '13px' }}><input type="checkbox" checked={participantForm.tripTo} onChange={e => setParticipantForm({ ...participantForm, tripTo: e.target.checked })} /> {t('events:tripToLabel')}</label>
                                            <label style={{ fontSize: '13px' }}><input type="checkbox" checked={participantForm.tripBack} onChange={e => setParticipantForm({ ...participantForm, tripBack: e.target.checked })} /> {t('events:tripBackLabel')}</label>
                                        </div>
                                        <input placeholder={t('events:note')} value={participantForm.note} onChange={e => setParticipantForm({ ...participantForm, note: e.target.value })} style={input} />
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button type="submit" style={primaryBtn}>{participantForm.id ? t('events:saveParticipant') : t('events:addPerson')}</button>
                                            {participantForm.id && <button type="button" onClick={() => setParticipantForm({ id: null, fullName: '', role: 'participant', groupId: '', tripTo: true, tripBack: true, ticketCost: '', transferCost: '', hotelCost: '', otherCost: '', note: '' })} style={ghostBtn}>{t('events:reset')}</button>}
                                        </div>
                                    </form>
                                    {participants.length === 0 ? (
                                        <div style={{ color: '#777' }}>{t('events:rosterEmpty')}</div>
                                    ) : (
                                        <div style={{ display: 'grid', gap: '6px' }}>
                                            {participants.map(p => (
                                                <div key={p.id} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <div style={{ fontWeight: 700 }}>{p.fullName}</div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            {p.role} · {(p.tripTo ? t('events:tripThere') : '')}{(p.tripBack ? t('events:tripBack') : '')}· {(p.totalCost || 0).toLocaleString('ru-RU')} ₽
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button onClick={() => setParticipantForm({
                                                            id: p.id, fullName: p.fullName || '', role: p.role || 'participant',
                                                            groupId: p.groupId ? String(p.groupId) : '', tripTo: !!p.tripTo, tripBack: !!p.tripBack,
                                                            ticketCost: p.ticketCost ?? '', transferCost: p.transferCost ?? '',
                                                            hotelCost: p.hotelCost ?? '', otherCost: p.otherCost ?? '', note: p.note || ''
                                                        })} style={ghostBtn}>{t('events:edit')}</button>
                                                        <button onClick={() => handleDeleteParticipant(p.id)} style={dangerBtn}>{t('events:delete')}</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            {eventTab === 'costs' && selectedEvent && (
                                <>
                                    <h3 style={{ marginTop: 0 }}>{t('events:costsTitle')}</h3>
                                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px', marginBottom: '10px' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('events:commonCostsTitle')}</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '4px', color: '#64748b', fontSize: '12px' }}>
                                            <div>{t('events:ticketsCommon')}</div>
                                            <div>{t('events:transferCommon')}</div>
                                            <div>{t('events:hotelCommon')}</div>
                                            <div>{t('events:otherBaseCommon')}</div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '8px' }}>
                                            <input type="number" min="0" step="0.01" placeholder={t('events:categories.ticket')} value={costsForm.ticketCost} onChange={e => setCostsForm({ ...costsForm, ticketCost: e.target.value })} style={input} />
                                            <input type="number" min="0" step="0.01" placeholder={t('events:categories.transfer')} value={costsForm.transferCost} onChange={e => setCostsForm({ ...costsForm, transferCost: e.target.value })} style={input} />
                                            <input type="number" min="0" step="0.01" placeholder={t('events:categories.hotel')} value={costsForm.hotelCost} onChange={e => setCostsForm({ ...costsForm, hotelCost: e.target.value })} style={input} />
                                            <input type="number" min="0" step="0.01" placeholder={t('events:categories.otherBase')} value={costsForm.otherCost} onChange={e => setCostsForm({ ...costsForm, otherCost: e.target.value })} style={input} />
                                        </div>
                                        <div style={{ border: '1px dashed #e2e8f0', borderRadius: '8px', padding: '8px', marginBottom: '8px' }}>
                                            <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('events:extraCostsTitle')}</div>
                                            {extraCosts.length === 0 && <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>{t('events:noExtraCosts')}</div>}
                                            {extraCosts.map((item, idx) => (
                                                <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '8px', marginBottom: '6px' }}>
                                                    <input
                                                        placeholder={t('events:expenseName')}
                                                        value={item.name}
                                                        onChange={e => {
                                                            const copy = [...extraCosts];
                                                            copy[idx] = { ...copy[idx], name: e.target.value };
                                                            setExtraCosts(copy);
                                                        }}
                                                        style={input}
                                                    />
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        placeholder={t('events:amount')}
                                                        value={item.amount}
                                                        onChange={e => {
                                                            const copy = [...extraCosts];
                                                            copy[idx] = { ...copy[idx], amount: e.target.value };
                                                            setExtraCosts(copy);
                                                        }}
                                                        style={input}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveExtraCostWithRecalc(item.id)}
                                                        style={dangerBtn}
                                                    >
                                                        {t('events:delete')}
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const id = String(Date.now() + Math.random());
                                                    setExtraCosts([...extraCosts, { id, name: '', amount: '' }]);
                                                    setBulkExtraValues({ ...bulkExtraValues, [id]: { mode: 'fixed', value: '' } });
                                                    const updated = { ...participantCostEdits };
                                                    participants.forEach((p) => {
                                                        if (!updated[p.id]) updated[p.id] = { ticketCost: '', transferCost: '', hotelCost: '', otherCost: '', extra: {} };
                                                        if (!updated[p.id].extra) updated[p.id].extra = {};
                                                        updated[p.id].extra[id] = '';
                                                    });
                                                    setParticipantCostEdits(updated);
                                                }}
                                                style={ghostBtn}
                                            >
                                                {t('events:addExtraCost')}
                                            </button>
                                        </div>
                                        <button onClick={handleUpdateCommonCosts} style={primaryBtn}>{t('events:saveCommonCosts')}</button>
                                    </div>

                                    <div style={{ border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '10px' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('events:bulkOpsTitle')}</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px' }}>
                                            <select value={bulkForm.role} onChange={e => setBulkForm({ ...bulkForm, role: e.target.value })} style={input}>
                                                <option value="">{t('events:allRoles')}</option>
                                                <option value="participant">{t('events:roleParticipants')}</option>
                                                <option value="staff">{t('events:roleStaffPlural')}</option>
                                                <option value="companion">{t('events:roleCompanions')}</option>
                                            </select>
                                            <select value={bulkForm.groupId} onChange={e => setBulkForm({ ...bulkForm, groupId: e.target.value })} style={input}>
                                                <option value="">{t('events:allGroups')}</option>
                                                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                            </select>
                                            <button onClick={handleApplyBulk} style={primaryBtn}>{t('events:apply')}</button>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                                            <input type="number" min="0" step="0.01" placeholder={t('events:ticketThere')} value={bulkForm.ticketTo} onChange={e => setBulkForm({ ...bulkForm, ticketTo: e.target.value })} style={input} />
                                            <input type="number" min="0" step="0.01" placeholder={t('events:ticketBack')} value={bulkForm.ticketBack} onChange={e => setBulkForm({ ...bulkForm, ticketBack: e.target.value })} style={input} />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '8px' }}>
                                            <select value={bulkForm.transferMode} onChange={e => setBulkForm({ ...bulkForm, transferMode: e.target.value })} style={input}><option value="fixed">{t('events:transferFixed')}</option><option value="split">{t('events:transferSplit')}</option></select>
                                            <input type="number" min="0" step="0.01" placeholder={t('events:amount')} value={bulkForm.transferValue} onChange={e => setBulkForm({ ...bulkForm, transferValue: e.target.value })} style={input} />
                                            <select value={bulkForm.hotelMode} onChange={e => setBulkForm({ ...bulkForm, hotelMode: e.target.value })} style={input}><option value="fixed">{t('events:hotelFixed')}</option><option value="split">{t('events:hotelSplit')}</option></select>
                                            <input type="number" min="0" step="0.01" placeholder={t('events:amount')} value={bulkForm.hotelValue} onChange={e => setBulkForm({ ...bulkForm, hotelValue: e.target.value })} style={input} />
                                            <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#64748b' }}>{t('events:otherCategory')}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#64748b' }}>{t('events:bulkDisabled')}</div>
                                        </div>
                                        {(extraCosts || []).length > 0 && (
                                            <div style={{ marginTop: '10px', borderTop: '1px dashed #dbeafe', paddingTop: '8px' }}>
                                                <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('events:bulkExtraTitle')}</div>
                                                {(extraCosts || []).map((x) => (
                                                    <div key={`bulk_extra_${x.id}`} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '6px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>{x.name || t('events:extraCostShort')}</div>
                                                        <select
                                                            value={bulkExtraValues[x.id]?.mode || 'fixed'}
                                                            onChange={e => setBulkExtraValues({ ...bulkExtraValues, [x.id]: { ...(bulkExtraValues[x.id] || { value: '' }), mode: e.target.value } })}
                                                            style={input}
                                                        >
                                                            <option value="fixed">{t('events:modeFixed')}</option>
                                                            <option value="split">{t('events:modeSplit')}</option>
                                                        </select>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            placeholder={t('events:amount')}
                                                            value={bulkExtraValues[x.id]?.value || ''}
                                                            onChange={e => setBulkExtraValues({ ...bulkExtraValues, [x.id]: { ...(bulkExtraValues[x.id] || { mode: 'fixed' }), value: e.target.value } })}
                                                            style={input}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px', marginTop: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <div style={{ fontWeight: 700 }}>{t('events:pointAdjustTitle')}</div>
                                            <button type="button" onClick={handleSaveAllParticipantCosts} style={primaryBtn}>{t('events:saveAll')}</button>
                                        </div>
                                        {participants.length === 0 ? (
                                            <div style={{ color: '#777' }}>{t('events:addParticipantsFirst')}</div>
                                        ) : (
                                            <div style={{ display: 'grid', gap: '6px' }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                                                    <div>{t('events:colParticipant')}</div>
                                                    <div>{t('events:colTickets')}</div>
                                                    <div>{t('events:colTransfer')}</div>
                                                    <div>{t('events:colHotel')}</div>
                                                    <div>{t('events:colOther')}</div>
                                                    <div></div>
                                                </div>
                                                {participants.map((p) => (
                                                    <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', gap: '8px', alignItems: 'center' }}>
                                                        <div style={{ fontSize: '13px' }}>
                                                            <div style={{ fontWeight: 700 }}>{p.fullName}</div>
                                                            <div style={{ color: '#64748b', fontSize: '12px' }}>{p.role}</div>
                                                        </div>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={participantCostEdits[p.id]?.ticketCost ?? ''}
                                                            onChange={e => setParticipantCostEdits({ ...participantCostEdits, [p.id]: { ...participantCostEdits[p.id], ticketCost: e.target.value } })}
                                                            style={input}
                                                        />
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={participantCostEdits[p.id]?.transferCost ?? ''}
                                                            onChange={e => setParticipantCostEdits({ ...participantCostEdits, [p.id]: { ...participantCostEdits[p.id], transferCost: e.target.value } })}
                                                            style={input}
                                                        />
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={participantCostEdits[p.id]?.hotelCost ?? ''}
                                                            onChange={e => setParticipantCostEdits({ ...participantCostEdits, [p.id]: { ...participantCostEdits[p.id], hotelCost: e.target.value } })}
                                                            style={input}
                                                        />
                                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{t('events:disabled')}</div>
                                                        <button type="button" onClick={() => handleSaveParticipantCosts(p.id)} style={primaryBtn}>{t('events:save')}</button>
                                                    </div>
                                                ))}
                                                {(extraCosts || []).length > 0 && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('events:pointExtraTitle')}</div>
                                                        {(extraCosts || []).map((x) => (
                                                            <div key={`row_extra_${x.id}`} style={{ marginBottom: '6px' }}>
                                                                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>{x.name || t('events:extraCostShort')}</div>
                                                                {participants.map((p) => (
                                                                    <div key={`row_extra_${x.id}_${p.id}`} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                                                                        <div style={{ fontSize: '13px' }}>{p.fullName}</div>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.01"
                                                                            value={participantCostEdits[p.id]?.extra?.[x.id] ?? ''}
                                                                            onChange={e => setParticipantCostEdits({
                                                                                ...participantCostEdits,
                                                                                [p.id]: {
                                                                                    ...(participantCostEdits[p.id] || { ticketCost: '', transferCost: '', hotelCost: '', otherCost: '', extra: {} }),
                                                                                    extra: { ...((participantCostEdits[p.id] && participantCostEdits[p.id].extra) || {}), [x.id]: e.target.value }
                                                                                }
                                                                            })}
                                                                            style={input}
                                                                        />
                                                                        <div style={{ fontSize: '12px', color: '#64748b' }}>₽</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {eventTab === 'receipts' && selectedEvent && (
                                <>
                                    <h3 style={{ marginTop: 0 }}>{t('events:receiptsTitle')}</h3>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                                        <input type="file" multiple onChange={e => setReceiptFiles(e.target.files)} />
                                        <button onClick={handleUploadReceipts} style={primaryBtn}>{t('events:uploadReceipts')}</button>
                                    </div>
                                    {(selectedEvent.receipts || []).length === 0 ? (
                                        <div style={{ color: '#777' }}>{t('events:receiptsEmpty')}</div>
                                    ) : (
                                        <div style={{ display: 'grid', gap: '6px' }}>
                                            {(selectedEvent.receipts || []).map((r, idx) => (
                                                <div key={`${r}_${idx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #eee', borderRadius: '8px', padding: '8px' }}>
                                                    <a href={r} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', textDecoration: 'none', fontSize: '13px' }}>{r}</a>
                                                    <button onClick={() => handleDeleteReceipt(r)} style={dangerBtn}>{t('events:delete')}</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '10px', paddingTop: '10px' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '8px' }}>{t('events:linkReceiptsTitle')}</div>
                                        {eventExpenseKeys.map((item) => (
                                            <div key={`event_expense_receipt_${item.key}`} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', marginBottom: '8px' }}>
                                                <div style={{ fontWeight: 700, marginBottom: '6px' }}>{item.label}</div>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                                                    <input type="file" multiple onChange={e => setCategoryUploadFiles({ ...categoryUploadFiles, [item.key]: e.target.files })} />
                                                    <button type="button" onClick={() => handleUploadCategoryReceipts(item.key)} style={ghostBtn}>{t('events:attachReceipt')}</button>
                                                </div>
                                                {((receiptMeta.categoryReceipts || {})[item.key] || []).length === 0 ? (
                                                    <div style={{ color: '#777', fontSize: '12px' }}>{t('events:noReceipts')}</div>
                                                ) : (
                                                    <div style={{ display: 'grid', gap: '4px' }}>
                                                        {((receiptMeta.categoryReceipts || {})[item.key] || []).map((path, idx) => (
                                                            <div key={`${item.key}_${idx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <a href={path} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', textDecoration: 'none', fontSize: '12px' }}>{path}</a>
                                                                <button type="button" onClick={() => handleDeleteReceipt(path)} style={dangerBtn}>{t('events:delete')}</button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '10px', paddingTop: '10px' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '8px' }}>{t('events:participantReceiptsTitle')}</div>
                                        {participants.length === 0 ? (
                                            <div style={{ color: '#777' }}>{t('events:noParticipants')}</div>
                                        ) : participants.map((p) => (
                                            <div key={`participant_receipt_bind_${p.id}`} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', marginBottom: '8px' }}>
                                                <div style={{ fontWeight: 700, marginBottom: '6px' }}>{p.fullName}</div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'center' }}>
                                                    <select value={participantReceiptCategory[p.id] || 'ticket'} onChange={e => setParticipantReceiptCategory({ ...participantReceiptCategory, [p.id]: e.target.value })} style={input}>
                                                        <option value="ticket">{t('events:categories.ticket')}</option>
                                                        <option value="transfer">{t('events:categories.transfer')}</option>
                                                        <option value="hotel">{t('events:categories.hotel')}</option>
                                                        <option value="other_base">{t('events:categories.otherBase')}</option>
                                                    </select>
                                                    <input type="file" multiple onChange={e => setParticipantUploadFiles({ ...participantUploadFiles, [p.id]: e.target.files })} />
                                                    <button type="button" onClick={() => handleUploadParticipantReceipts(p.id)} style={ghostBtn}>{t('events:attachReceipt')}</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {eventTab === 'totals' && selectedEvent && (
                                <>
                                    <h3 style={{ marginTop: 0 }}>{t('events:totalsTitle')}</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
                                        <Kpi label={t('events:kpiPeople')} value={participants.length} />
                                        <Kpi label={t('events:kpiCommonCosts')} value={`${commonTotal.toLocaleString('ru-RU')} ₽`} />
                                        <Kpi label={t('events:kpiPersonalCosts')} value={`${participantsTotal.toLocaleString('ru-RU')} ₽`} />
                                        <Kpi label={t('events:kpiTotal')} value={`${(commonTotal + participantsTotal).toLocaleString('ru-RU')} ₽`} />
                                    </div>
                                    <div style={{ marginTop: '10px', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '8px' }}>{t('events:totalsEventCosts')}</div>
                                        {eventExpenseKeys.map((item) => (
                                            <div key={`event_total_${item.key}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                                                <div>{item.label}</div>
                                                <div>{((receiptMeta.categoryReceipts || {})[item.key] || []).length > 0 ? t('events:receiptAttached') : t('events:receiptMissing')}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ marginTop: '10px', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '8px' }}>{t('events:totalsParticipantCosts')}</div>
                                        {participants.map((p) => {
                                            const partMap = (receiptMeta.participantCategoryReceipts || {})[String(p.id)] || {};
                                            return (
                                                <div key={`participant_total_${p.id}`} style={{ borderBottom: '1px solid #f1f5f9', padding: '6px 0', fontSize: '13px' }}>
                                                    <div style={{ fontWeight: 700 }}>{p.fullName}</div>
                                                    <div style={{ color: '#64748b', fontSize: '12px' }}>
                                                        {t('events:participantCostsLine', {
                                                            ticket: (p.ticketCost || 0).toLocaleString('ru-RU'),
                                                            ticketReceipt: (partMap.ticket || []).length > 0 ? t('events:receiptYes') : t('events:receiptNo'),
                                                            transfer: (p.transferCost || 0).toLocaleString('ru-RU'),
                                                            transferReceipt: (partMap.transfer || []).length > 0 ? t('events:receiptYes') : t('events:receiptNo'),
                                                            hotel: (p.hotelCost || 0).toLocaleString('ru-RU'),
                                                            hotelReceipt: (partMap.hotel || []).length > 0 ? t('events:receiptYes') : t('events:receiptNo')
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
            </OwnerPageLayout>
        </>
    );
}

const card = { background: '#fff', borderRadius: '12px', border: '1px solid var(--sp-gray-100)', padding: '14px' };
const input = { width: '100%', border: '1px solid #e5e7eb', borderRadius: '9px', padding: '9px 10px', fontSize: '14px', boxSizing: 'border-box' };
const primaryBtn = { border: 'none', borderRadius: '8px', padding: '8px 12px', background: '#1d4ed8', color: 'white', cursor: 'pointer', fontSize: '13px' };
const dangerBtn = { border: 'none', borderRadius: '8px', padding: '6px 10px', background: '#b91c1c', color: 'white', cursor: 'pointer', fontSize: '12px' };
const ghostBtn = { border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px', background: '#fff', color: '#334155', cursor: 'pointer', fontSize: '13px' };
const tabBtn = (active) => ({ border: '1px solid var(--sp-gray-200)', borderRadius: '100px', padding: '8px 18px', background: active ? 'var(--sp-primary)' : '#fff', color: active ? '#fff' : 'var(--sp-gray-600)', cursor: 'pointer', fontSize: '13px', fontWeight: 700 });

function Kpi({ label, value }) {
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px', background: '#fff' }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>{label}</div>
            <div style={{ fontSize: '18px', fontWeight: 800 }}>{value}</div>
        </div>
    );
}

function normalizeReceiptMeta(meta) {
    const m = meta || {};
    const extraRaw = m.extraCosts || m.ExtraCosts || [];
    const normalizeExtra = extraRaw.map((x) => ({
        id: String(x.id ?? x.Id ?? `${Date.now()}_${Math.random()}`),
        name: String(x.name ?? x.Name ?? ''),
        amount: x.amount ?? x.Amount ?? ''
    }));
    return {
        receipts: m.receipts || m.Receipts || [],
        extraCosts: normalizeExtra,
        categoryReceipts: m.categoryReceipts || m.CategoryReceipts || {},
        participantCategoryReceipts: m.participantCategoryReceipts || m.ParticipantCategoryReceipts || {}
    };
}
